<template>
  <component
    v-model.trim="inputValue"
    ref="input"
    :is="componentType"
    :type="type"
    :label="label"
    :name="name"
    :readonly="readonly"
    :placeholder="placeholder"
    :icon="icon"
    :mask-regex="maskRegex"
    :disabled="disabled"
    :limit-max-length="limitMaxLength"
    :multiple="multiple"
    :accept="accept"
    :unmask-on-input="unmaskOnInput"
    :max-file-size="maxFileSize"
    :max-file-count="maxFileCount"
    :min-length="minLength"
    :max-length="maxLength"
    :required="required"
    :pattern="pattern"
    :email="email"
    :server="server"
    :same-as="sameAs"
    :not="not"
    :max-value="maxValue"
    :min-value="minValue"
    :width="width"
    :loading="loading"
    :autocomplete="autocomplete"
    :custom-validation="customValidation"
    :withTime="withTime"
    :onlyTime="onlyTime"
    :minDate="minDate"
    :maxDate="maxDate"
    @input="handleInput"
    @blur="$emit('blur')"
    @focus="$emit('focus')"
    @keydown="$emit('keydown')"
    @keyup="$emit('keyup')"
    @keypress="$emit('keypress')"
    @change="$emit('change')"
    @keydown.enter="$emit('enter')"
    @paste="$emit('paste')"
  >
    <slot />
  </component>
</template>

<script>
import Vue from 'vue'
import InputProps from './Base/InputProps'

import VText from './Text.vue'
import VTextArea from './Textarea.vue'
import VPassword from './Password.vue'
import { MIXIN_DEFINE_PROP } from '@/mixins/form/input/mixinDefineProp'

const componentMap = {
  textarea: 'VTextArea',
  password: 'VPassword'
}

export default Vue.extend({
  name: 'VInput',
  inject: { formData: { from: 'formData', default: null } },
  emits: ['enter'],
  extends: InputProps,
  components: {
    VText,
    VTextArea,
    VPassword
  },
  mixins: [MIXIN_DEFINE_PROP],
  props: {
    maskRegex: [String, RegExp],
    required: Boolean,
    server: Function,
    pattern: [String, RegExp],
    minLength: String,
    maxLength: String,
    sameAs: String,
    not: String,
    icon: String,
    maxValue: [Number, null],
    minValue: [Number, null],
    email: Boolean,
    unmaskOnInput: {
      type: Boolean,
      default: false
    },
    customValidation: {
      type: [Function, null],
      default: null
    },
    validateOnInput: {
      type: Boolean,
      default: false
    },

    // for file type input
    fileName: {
      type: String,
      default: ''
    },
    maxFileSize: {
      type: Number,
      default: Infinity
    },
    maxFileCount: {
      type: Number,
      default: Infinity
    },
    accept: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },

    // for date
    minDate: {
      type: [String, Date],
      default: null
    },
    maxDate: {
      type: [String, Date],
      default: null
    },
    withTime: {
      type: Boolean,
      default: false
    },
    onlyTime: {
      type: Boolean,
      default: false
    }
  },
  model: {
    prop: 'value',
    event: 'valueChange'
  },
  computed: {
    componentType () {
      const componentKey = Object.keys(componentMap).find(
        (key) => this.type === key
      )

      if (!componentKey) {
        return 'VText'
      } else {
        return componentMap[componentKey]
      }
    },
    inputValue: {
      get () {
        if (this.name && this.formData) {
          return this.formData[this.name]
        } else {
          return this.value
        }
      },
      set (val) {
        if (this.name && this.formData) {
          Vue.set(this.formData, this.name, val)
        }
        this.$emit('valueChange', val)
      }
    },
    hasValidationError () {
      return this.$refs.input.hasValidationError
    }
  },
  methods: {
    touch () {
      const touchValidator = this.$refs.input.$v?.$touch
      touchValidator && touchValidator()
    },
    focus () {
      this.$refs.input.focus()
    },
    handleInput (val) {
      if (this.validateOnInput) this.touch()
      this.$emit('input', val)
    }
  }
})
</script>
